import { ComponentFactory } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-factory';
import { IWrapperComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/types';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { Container } from '@tencent/alloy-renderer';
import { transaction } from 'mobx';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';

// buildTreeSync的参数
export interface IBuildTreeSyncParams {
    canvasLayer: Container, // 挂载图形元素的component，后续节点的增删对此进行操作
    canvasArr: IWrapperComponent[], // canvasLayer的children
    modelsArr: IBaseModel[], // models数组
    componentFactoryOptions: InterfaceFactoryOptions<null>, // 创建新节点时，new ComponentFactory的参数
    componentNeedUpdate?: boolean, // canvas节点在非新建状态即（move or 不动）是否需要更新  slide场景不需要，group场景需要
}

// 获取canvas树children与models的key数组以及相应对象map
function getKeyMapAndArr(canvasData, modelsData) {
    const canvasKeyMap = {};
    const canvasKeyArr = canvasData.map((e) => {
        const key = e?.factoryOptions?.model?.key;
        canvasKeyMap[key] = e;
        return key;
    });
    const modelsKeyMap = {};
    const modelsKeyArr = modelsData.map((e) => {
        const key = e?.key;
        modelsKeyMap[key] = e;
        return key;
    });
    return { canvasKeyMap, canvasKeyArr, modelsKeyMap, modelsKeyArr };
}

// 触发canvas节点的刷新
function updateComponent(element: IWrapperComponent, componentNeedUpdate: boolean): void {
    if (componentNeedUpdate) {
        element.factoryUpdateComponent();
    }
}

/**
 * models更新后，差量对canvasLayer上的节点进行增删或者移动，使得两者同步
 * @param params
 */
export function buildTreeSync(params: IBuildTreeSyncParams): void {
    const {
        canvasLayer,
        canvasArr = [],
        modelsArr = [],
        componentFactoryOptions,
        componentNeedUpdate = false,
    } = params;
    const { canvasKeyMap, canvasKeyArr, modelsKeyMap, modelsKeyArr } = getKeyMapAndArr(canvasArr, modelsArr);
    // 如果所有节点位置均没有变化，且无需更新，则不继续后续操作
    if (!componentNeedUpdate && canvasKeyArr.toString() === modelsKeyArr.toString()) {
        return;
    }
    const startTime = Date.now();
    transaction(() => {
        try {
            // (1) 在canvas节点删除在models已不存在的子节点
            canvasArr.slice().forEach((child) => {
                if (child?.wrapperInstance
                    && child?.factoryOptions?.model?.key
                    && !modelsKeyMap[child?.factoryOptions?.model?.key]) {
                    canvasLayer.removeChild(child);
                    child.dispose();
                }
            });
            // (2) 对照models在canvas新增或者调整节点(经过步骤1，canvas节点长度必然<=models长度)
            modelsArr.slice().forEach((model, index) => {
                if (canvasLayer.children.length <= index) {
                    // models的此节点index已超出canvas节点长度，说明肯定是新节点则无脑新建即可
                    const insertElement = new ComponentFactory({ ...componentFactoryOptions, model }).getWrapper();
                    canvasLayer.addChildAt(insertElement, index);
                } else {
                    // models的此节点index在canvas节点长度范围内
                    const canvasItem = canvasLayer.children[index] as IWrapperComponent;
                    const canvasItemKey = canvasItem?.factoryOptions?.model?.key;
                    if (canvasItemKey !== model.key) {
                        // 节点不是同一个
                        if (canvasKeyMap[model.key]) {
                            // 如果canvas树上有则无需新建，直接摘下来插到此位置（此处无需销毁事件）
                            const moveElement = canvasKeyMap[model.key];
                            canvasLayer.removeChild(moveElement);
                            canvasLayer.addChildAt(moveElement, index);
                            updateComponent(moveElement, componentNeedUpdate);
                        } else {
                            // 如果canvas树上没有则新建
                            const insertElement = new ComponentFactory({
                                ...componentFactoryOptions,
                                model }).getWrapper();
                            canvasLayer.addChildAt(insertElement, index);
                        }
                    } else {
                        // 节点是同一个
                        updateComponent(canvasItem, componentNeedUpdate);
                    }
                }
            });
        } catch (e) {
            window.log.error(`[canvas-sync-build-tree]:${e}`);
            tdw({
                opername: TDWOperationType.PPT_QUALITY,
                module: TDWModuleType.CANVAS_RENDERER,
                action: TDWActionType.CANVAS_RENDERER_BUILD_TREE,
                ver6: JSON.stringify({ errorMsg: e.message, async: 0 }),
            });
        }
    });
    // 数据采集
    if (window && !window.firstCanvasBuildTreeTime) {
        window.firstCanvasBuildTreeTime = Date.now() - startTime;
    }
}
